"use client";

import { Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { LoadingOverlay } from "@/app/components/shared/LoadingOverlay";
import { LOADING_LABEL } from "./constants";

/**
 * Decide si un click de ratón dispara una navegación interna real (excluye
 * `target="_blank"`, descargas, enlaces externos, clicks modificados/no
 * izquierdos y anclas que solo cambian el hash de la página actual).
 */
function isInternalNavigationClick(event: MouseEvent): boolean {
  if (event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
    return false;

  const anchor = (event.target as Element | null)?.closest?.("a[href]");
  if (!(anchor instanceof HTMLAnchorElement)) return false;
  if (anchor.target && anchor.target !== "_self") return false;
  if (anchor.hasAttribute("download")) return false;

  let destination: URL;
  try {
    destination = new URL(anchor.href, window.location.href);
  } catch {
    return false;
  }

  if (destination.origin !== window.location.origin) return false;

  const isSameLocation =
    destination.pathname === window.location.pathname &&
    destination.search === window.location.search;
  return !isSameLocation;
}

function NavigationProgressWatcher(): ReactNode {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isNavigating, setIsNavigating] = useState(false);
  const locationKeyRef = useRef(`${pathname}?${searchParams.toString()}`);

  // La navegación termina cuando la ruta/query resultante cambia.
  useEffect(() => {
    const locationKey = `${pathname}?${searchParams.toString()}`;
    if (locationKey !== locationKeyRef.current) {
      locationKeyRef.current = locationKey;
      setIsNavigating(false);
    }
  }, [pathname, searchParams]);

  useEffect(() => {
    function handleClick(event: MouseEvent): void {
      if (isInternalNavigationClick(event)) {
        setIsNavigating(true);
      }
    }

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  if (!isNavigating) return null;

  return <LoadingOverlay label={LOADING_LABEL} />;
}

/**
 * NavigationProgress — muestra el overlay de carga general
 * (`LoadingOverlay`) mientras se completa la navegación entre páginas, tras el
 * click en un enlace interno.
 *
 * Client Component: depende de listeners de DOM (`click`) y de los hooks de
 * navegación (`usePathname`/`useSearchParams`), inexistentes en Server
 * Components. `useSearchParams` exige su propio límite `Suspense` (si no,
 * Next.js fuerza el render cliente de toda la ruta que lo consuma), por eso
 * la lógica vive en un componente interno envuelto aquí.
 */
export function NavigationProgress(): ReactNode {
  return (
    <Suspense fallback={null}>
      <NavigationProgressWatcher />
    </Suspense>
  );
}
